import ExifReader from 'exifreader';

/**
 * GPS coordinates (latitude, longitude, optional altitude) extracted from an image's EXIF metadata.
 */
export interface GpsMetadata {
  latitude: number;
  longitude: number;
  altitude?: number;
}

/**
 * Structured metadata extracted from an image's EXIF data, including GPS coordinates, source, and extraction timestamp.
 */
export interface ImageMetadataPayload {
  gps: GpsMetadata;
  source: 'exif';
  extractedAt: string; // ISO timestamp
  fileName: string;
  takenDateTime?: string; // ISO if present in EXIF
}

// helpers
const toNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number.parseFloat(v.replace(/[^0-9.+-]/g, ''));
    return Number.isFinite(n) ? n : undefined;
  }
  if (Array.isArray(v) && v.length) return toNumber(v[0]);
  return undefined;
};
const firstString = (v: unknown): string | undefined => {
  if (typeof v === 'string') return v;
  if (Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0];
  return undefined;
};
const normalizeRef = (s?: string): 'N' | 'S' | 'E' | 'W' | undefined => {
  if (!s) return undefined;
  const t = s.trim().toUpperCase();
  if (t === 'N' || t === 'S' || t === 'E' || t === 'W') return t;
  if (t.startsWith('NORTH')) return 'N';
  if (t.startsWith('SOUTH')) return 'S';
  if (t.startsWith('EAST')) return 'E';
  if (t.startsWith('WEST')) return 'W';
  return undefined;
};

/**
 * Extract GPS EXIF metadata for a File (image) and return JSON string or null if no GPS.
 * Applies hemisphere refs (N/S for latitude, E/W for longitude). W => negative longitude.
 */
export async function extractGpsMetadataJson(file: File): Promise<string | null> {
  try {
    const buffer = await file.arrayBuffer();
    const tags = ExifReader.load(buffer) as any;

    // Decimal degrees from description or value
    const lat = toNumber(tags.GPSLatitude?.description ?? tags.GPSLatitude?.value);
    const lon = toNumber(tags.GPSLongitude?.description ?? tags.GPSLongitude?.value);
    if (lat == null || lon == null) return null;

    // Hemisphere refs may be arrays or descriptive strings
    const latRef = normalizeRef(firstString(tags.GPSLatitudeRef?.value) ?? firstString(tags.GPSLatitudeRef?.description));
    const lonRef = normalizeRef(firstString(tags.GPSLongitudeRef?.value) ?? firstString(tags.GPSLongitudeRef?.description));

    let latitude = lat;
    let longitude = lon;
    if (latRef === 'S') latitude = -Math.abs(latitude);
    if (latRef === 'N') latitude = Math.abs(latitude);
    if (lonRef === 'W') longitude = -Math.abs(longitude); // West negative
    if (lonRef === 'E') longitude = Math.abs(longitude);  // East positive

    // Altitude and ref (1 = below sea level)
    let altitude = toNumber(tags.GPSAltitude?.description ?? tags.GPSAltitude?.value);
    let altRef = toNumber(tags.GPSAltitudeRef?.value);
    if (altRef == null) {
      const altDesc = firstString(tags.GPSAltitudeRef?.description)?.toLowerCase();
      if (altDesc?.includes('below')) altRef = 1;
      if (altDesc?.includes('above')) altRef = 0;
    }
    if (altitude != null && altRef === 1) altitude = -Math.abs(altitude);

    // Date/time
    const takenRaw = (firstString(tags.DateTimeOriginal?.description) || firstString(tags.DateTime?.description) || '').trim();
    let takenIso: string | undefined;
    if (takenRaw) {
      const normalized = takenRaw.replace(/^([0-9]{4}):([0-9]{2}):([0-9]{2}) /, '$1-$2-$3T');
      const isoCandidate = normalized.endsWith('Z') ? normalized : normalized + 'Z';
      if (!Number.isNaN(Date.parse(isoCandidate))) takenIso = new Date(isoCandidate).toISOString();
    }

    const payload: ImageMetadataPayload = {
      gps: { latitude, longitude, ...(altitude != null ? { altitude } : {}) },
      source: 'exif',
      extractedAt: new Date().toISOString(),
      fileName: file.name,
      ...(takenIso ? { takenDateTime: takenIso } : {})
    };
    return JSON.stringify(payload);
  } catch {
    return null;
  }
}
